<template>
  <PageWrapper>
    <div class="container">
      <div class="row">
        <div class="c-lg:col-10 c-xl:col-8 c-mx-auto">
          <BreadCrumb
            :breadcrumb-list="breadcrumbList"
            additional-class-name="c-justify-center"
          />
          <h1
            v-if="!searchText"
            class="h2 text-center c-mt-4"
          >
            {{ t('write_what_you_are_looking') }}
          </h1>
          <h1
            v-else
            class="h2 text-center c-mt-4"
          >
            {{ t('search_results', { value: searchText }) }}
          </h1>
        </div>
        <div class="w-100 flex c-md:c-justify-between c-md:c-flex-row c-flex-col c-mb-4">
          <Search
            type="navbar"
            :init-value="urlSearchValue"
            :items="products"
            :hide-whisperer="true"
            class="c-md:col-7 c-xl:col-5"
            @search-changed="searchChanged"
            @enter-pressed="searchChanged"
          />
          <button
            class="btn bg-transparent c-mt-5 c-md:c-mt-0 c-md:c-ml-5"
            @click="sortSearchByAvailability"
          >
            <i class="mi-arrow-u" />
            <i class="mi-arrow-d ml-n2 c-mr-2" />
            {{ t('sort_by_availability') }}
          </button>
        </div>
      </div>
      <div v-if="products.length > 0">
        <ProductsCatalogGrid
          :is-loaded="isLoaded"
          :products="products"
          info-click-behaviour="modal"
          :is-search-page="true"
        />
      </div>
      <div v-else-if="searchText && isLoaded">
        <p class="text-center text-lg-c">
          {{ t('we_did_not_find') }}
        </p>
        <BaseCategoriesSection
          :title="t('choose_from_our_categories')"
          class="mt-n6"
        />
      </div>
      <div
        v-else-if="products.length === 0 && !searchText"
        class="flex c-flex-col c-items-center mt-n8"
      >
        <BaseCategoriesSection :title="t('or_choose_from_our_categories')" />
      </div>
      <BaseLoading
        v-else
        :style="{ minHeight: '30vh' }"
      />
    </div>
  </PageWrapper>
  <BaseModal modal-id="product-detail-modal">
    <ModalProductDetail />
  </BaseModal>
  <BaseModal
    :title="t('watchdog_title')"
    modal-id="modal-watchdog"
  >
    <ModalWatchdog />
  </BaseModal>
</template>

<script setup>
import { useDebounceFn } from '@vueuse/core'
import { defineAsyncComponent, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import RetailApiV2 from '@/api/retailApi/RetailApiV2'
import useHead from '@/mixins/headManager'
import { useNavbarStore } from '@/stores/NavbarStore'

const BreadCrumb = defineAsyncComponent(() => import('@/components/general/BaseBreadcrumb.vue'))
const BaseLoading = defineAsyncComponent(() => import('@/components/general/BaseLoading.vue'))
const BaseModal = defineAsyncComponent(() => import('@/components/general/BaseModal.vue'))
const PageWrapper = defineAsyncComponent(() => import('@/components/general/BasePageWrapper.vue'))
const Search = defineAsyncComponent(() => import('@/components/general/BaseSearch.vue'))
const ModalWatchdog = defineAsyncComponent(() => import('@/components/modals/ModalWatchdog.vue'))
const ModalProductDetail = defineAsyncComponent(() => import('@/components/modals/ModalProductDetail.vue'))
const ProductsCatalogGrid = defineAsyncComponent(() => import('@/components/products/ProductsCatalogGrid.vue'))
const BaseCategoriesSection = defineAsyncComponent(() => import('@/components/general/BaseCategoriesSection.vue'))

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const baseUrl = import.meta.env.VITE_VUE_APP_ORIGINAL_URL
const url = (baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl) + route.path
const isLoaded = ref(false)
const products = ref([])
const searchText = ref()
const urlSearchValue = route.params.search
const searchChanged = useDebounceFn(
  (value) => {
    search(value, true)
  },
  100,
  { maxWait: 15_000 },
)

const { setDefault } = useNavbarStore()
setDefault()
const search = async (value, historyPush = true) => {
  searchText.value = value
  isLoaded.value = false
  if (historyPush) {
    await router.push({ name: 'searchPath', params: { search: value } })
  }
  if (value.length > 0) {
    const { data } = await RetailApiV2.get(`search?q=${value}&limit=50&variant=lp`)
    products.value = data.products
    isLoaded.value = true
  } else {
    products.value = []
    isLoaded.value = false
  }
}
search(route.params.search, false)

const sortSearchByAvailability = () => {
  products.value.sort((a, b) => (b.state === 'available') - (a.state === 'available'));
}

const breadcrumbList = ref([
  {
    name: t('title'),
    link: { name: 'mixitAfiliatePath' },
  },
])

useHead({
  title: t('title'),
  link: [
    {
      rel: 'canonical',
      href: url,
    },
    {
      rel: 'alternate',
      hreflang: 'cs',
      href: 'https://www.mixit.cz/vernostni-program',
    },
    {
      rel: 'alternate',
      hreflang: 'sk',
      href: 'https://www.mixit.sk/vernostny-program',
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: 'https://www.mixit.pl/program-lojalnosciowy',
    },
    {
      rel: 'alternate',
      hreflang: 'hu',
      href: 'https://www.mixit.hu/husegprogram',
    },
    {
      rel: 'alternate',
      hreflang: 'ro',
      href: 'https://www.mixit.ro/program-loialitate',
    },
  ],
})
</script>

<i18n>
  {
    "cs": {
    "choose_from_our_categories": "Vybírejte z našich kategorií.",
    "or_choose_from_our_categories": "Nebo si vyberte z našich kategorií:",
    "search_results": "Výsledky vyhledávání: %{value}",
    "sort_by_availability": "Seřadit dle dostupnosti",
    "title": "Vyhledávání",
    "watchdog_title": "Chcete si počkat?",
    "we_did_not_find": "Bohužel tohle zobání jsme nenašli.",
    "write_what_you_are_looking": "Napište, jaké dobroty hledáte"
},
    "sk": {
    "choose_from_our_categories": "Vyberte si z našich kategórií.",
    "or_choose_from_our_categories": "Alebo si vyberte z našich kategórií:",
    "search_results": "Výsledky vyhľadávania: %{value}",
    "sort_by_availability": "Zoradiť podľa dostupnosti",
    "title": "Vyhľadávanie",
    "watchdog_title": "Chcete si počkať?",
    "we_did_not_find": "Bohužiaľ toto zobkanie sme nenašli.",
    "write_what_you_are_looking": "Napíšte, aké dobroty hľadáte"
},
    "pl": {
    "choose_from_our_categories": "Wybierz jedną z naszych kategorii.",
    "or_choose_from_our_categories": "Lub wybierz jedną z naszych kategorii:",
    "search_results": "Wyniki wyszukiwania: %{value}",
    "sort_by_availability": "Sortuj według dostępności",
    "title": "Szukaj",
    "watchdog_title": "Chcesz poczekać?",
    "we_did_not_find": "Niestety, nie znaleźliśmy takiego produktu",
    "write_what_you_are_looking": "Napisz, jakich przekąsek szukasz"
},
    "hu": {
    "choose_from_our_categories": "Válassz kategóriáink közül.",
    "or_choose_from_our_categories": "Vagy válassz kategóriáink közül:",
    "search_results": "Keresési eredmények: %{value}",
    "sort_by_availability": "Rendezés elérhetőség szerint",
    "title": "Keresés",
    "watchdog_title": "Szeretnél várni?",
    "we_did_not_find": "Ezt a finomságot sajnos nem találtuk meg.",
    "write_what_you_are_looking": "Írd le, milyen finomságokat keresel"
},
    "de": {
    "choose_from_our_categories": "Wähle aus unseren Kategorien.",
    "or_choose_from_our_categories": "oder wähle aus unseren Kategorien:",
    "search_results": "Suchergebnisse:%{value}",
    "sort_by_availability": "Nach Verfügbarkeit sortieren",
    "title": "Suche",
    "watchdog_title": "Möchtest du warten?",
    "we_did_not_find": "Leider haben wir dieses Produkt nicht gefunden",
    "write_what_you_are_looking": "Schreib uns, welche Leckereien du suchst"
},
    "at": {
    "choose_from_our_categories": "Wähle aus unseren Kategorien.",
    "or_choose_from_our_categories": "oder wähle aus unseren Kategorien:",
    "search_results": "Suchergebnisse:%{value}",
    "sort_by_availability": "Nach Verfügbarkeit sortieren",
    "title": "Suche",
    "watchdog_title": "Möchtest du warten?",
    "we_did_not_find": "Leider haben wir dieses Produkt nicht gefunden",
    "write_what_you_are_looking": "Schreib uns, welche Leckereien du suchst"
},
    "ro": {
    "choose_from_our_categories": "Alegeți din categoriile noastre.",
    "or_choose_from_our_categories": "Sau alegeți din categoriile noastre:",
    "search_results": "Rezultatele căutării: %{value}",
    "sort_by_availability": "Sortează după disponibilitate",
    "title": "Căutare",
    "watchdog_title": "Dorești să aștepți?",
    "we_did_not_find": "Din păcate, nu am găsit această gustare.",
    "write_what_you_are_looking": "Scrieți ce bunătăți căutați"
},
    "fr": {
    "choose_from_our_categories": "Choisissez parmi nos catégories.",
    "or_choose_from_our_categories": "Ou choisissez parmi nos catégories :",
    "search_results": "Résultats de recherche : %{value}",
    "sort_by_availability": "Trier par disponibilité",
    "title": "Recherche",
    "watchdog_title": "Voulez-vous patienter ?",
    "we_did_not_find": "Malheureusement, nous n'avons pas trouvé ce produit",
    "write_what_you_are_looking": "Dites-nous quelle gourmandise vous cherchez"
},
    "it": {
    "choose_from_our_categories": "Scegli tra le nostre categorie.",
    "or_choose_from_our_categories": "Oppure scegli tra le nostre categorie:",
    "search_results": "Risultati della ricerca:%{value}",
    "sort_by_availability": "Ordina per disponibilità",
    "title": "Ricerca",
    "watchdog_title": "Vuoi aspettare?",
    "we_did_not_find": "Purtroppo non abbiamo trovato questo spuntino.",
    "write_what_you_are_looking": "Scrivi quali prelibatezze stai cercando"
}
  }
</i18n>
